import {
  AfterViewInit,
  Component,
  ComponentRef,
  DestroyRef,
  EnvironmentInjector,
  HostListener,
  NgModuleRef,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  createNgModule,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FloatingNotesStateService } from '../shared/collaboration/floating-notes-state.service';
import { SidebarService } from '../shared/collaboration/sidebar.service';
import type { SidebarContainerComponent } from '../shared/overlays/sidebar-container/sidebar-container.component';

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

@Component({
  selector: 'app-lazy-shell-overlays',
  standalone: false,
  template: `
    <div class="quick-access-swipe-edge" aria-hidden="true"></div>
    @if (loading()) {
      <div class="lazy-shell-status" role="status" aria-live="polite">
        Cargando paneles opcionales...
      </div>
    }
    @if (loadError()) {
      <div class="lazy-shell-status lazy-shell-status--error" role="alert">
        <span>No se pudieron cargar los paneles opcionales.</span>
        <button type="button" (click)="retry()">Reintentar</button>
      </div>
    }
  `,
  styles: [`
    :host {
      display: contents;
    }

    .quick-access-swipe-edge {
      display: none;
    }

    @media (max-width: 991px) and (hover: none), (max-width: 991px) and (pointer: coarse) {
      .quick-access-swipe-edge {
        display: block;
        height: 16px;
        inset-inline: 0;
        position: fixed;
        top: 0;
        touch-action: none;
        z-index: 20;
      }
    }

    .lazy-shell-status {
      align-items: center;
      background: var(--surface-card);
      border: 1px solid var(--border-control);
      color: var(--text-primary);
      border-radius: 0.5rem;
      bottom: 1rem;
      display: flex;
      gap: 0.75rem;
      left: 1rem;
      padding: 0.75rem 1rem;
      position: fixed;
      z-index: 1500;
    }

    .lazy-shell-status--error {
      border-color: var(--status-danger-fg);
    }
  `],
})
export class LazyShellOverlaysComponent implements AfterViewInit, OnInit, OnDestroy {
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sidebarService = inject(SidebarService);
  private readonly floatingNotesService = inject(FloatingNotesStateService);
  private notesModuleRef: NgModuleRef<unknown> | null = null;
  private sidebarModuleRef: NgModuleRef<unknown> | null = null;
  private sidebarComponentRef: ComponentRef<SidebarContainerComponent> | null = null;
  private notesLoadPromise: Promise<void> | null = null;
  private sidebarLoadPromise: Promise<void> | null = null;
  private pendingLoads = 0;
  private lastLoadError: 'notes' | 'sidebar' | null = null;
  private idleHandle: number | null = null;
  private readonly mobileBreakpoint = 992;
  private readonly topSwipeEdge = 16;
  private readonly verticalSwipeThreshold = 140;
  private readonly maxHorizontalSwipeOffset = 90;
  private touchStartX: number | null = null;
  private touchStartY: number | null = null;
  private swipeGestureActive = false;

  readonly loading = signal(false);
  readonly loadError = signal(false);

  ngOnInit(): void {
    this.sidebarService.overlayLoadRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.loadSidebarUi());

    this.sidebarService.agendaOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isOpen) => {
        if (isOpen) {
          void this.loadSidebarUi();
        }
      });

    this.sidebarService.tasksOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isOpen) => {
        if (isOpen) {
          void this.loadSidebarUi();
        }
      });

    this.floatingNotesService.widgetLoadRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.loadNotesUi());
  }

  ngAfterViewInit(): void {
    // Notes were visible before the migration, so keep them ready after the
    // first render without putting their implementation in the initial graph.
    if (this.floatingNotesService.visible) {
      this.scheduleIdleLoad();
    }
  }

  ngOnDestroy(): void {
    this.cancelIdleLoad();
    this.notesModuleRef?.destroy();
    this.sidebarModuleRef?.destroy();
    this.notesModuleRef = null;
    this.sidebarModuleRef = null;
    this.sidebarComponentRef = null;
  }

  @HostListener('document:pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (!this.isMobileTouchGesture(event) || this.swipeGestureActive) {
      this.resetTouchGesture();
      return;
    }
    this.swipeGestureActive = true;
    this.touchStartX = event.clientX;
    this.touchStartY = event.clientY;
  }

  @HostListener('document:pointerup', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.swipeGestureActive || this.touchStartX === null || this.touchStartY === null) {
      this.resetTouchGesture();
      return;
    }

    const deltaX = event.clientX - this.touchStartX;
    const deltaY = event.clientY - this.touchStartY;
    const startedNearTopEdge = this.touchStartY <= this.topSwipeEdge;
    const isVerticalGesture = deltaY >= this.verticalSwipeThreshold
      && Math.abs(deltaX) <= this.maxHorizontalSwipeOffset;
    this.resetTouchGesture();

    if (startedNearTopEdge && isVerticalGesture) {
      void this.openQuickAccessFromGesture();
    }
  }

  @HostListener('document:pointercancel')
  onPointerCancel(): void {
    this.resetTouchGesture();
  }

  retry(): void {
    if (this.lastLoadError === 'sidebar') {
      void this.loadSidebarUi();
      return;
    }
    void this.loadNotesUi();
  }

  private scheduleIdleLoad(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const idleWindow = window as IdleWindow;
    if (idleWindow.requestIdleCallback) {
      this.idleHandle = idleWindow.requestIdleCallback(
        () => void this.loadNotesUi(),
        { timeout: 3000 },
      );
      return;
    }

    this.idleHandle = window.setTimeout(() => void this.loadNotesUi(), 2000);
  }

  private cancelIdleLoad(): void {
    if (this.idleHandle === null || typeof window === 'undefined') {
      return;
    }

    const idleWindow = window as IdleWindow;
    if (idleWindow.cancelIdleCallback) {
      idleWindow.cancelIdleCallback(this.idleHandle);
    } else {
      window.clearTimeout(this.idleHandle);
    }
    this.idleHandle = null;
  }

  private loadNotesUi(): Promise<void> {
    if (this.notesModuleRef) {
      return Promise.resolve();
    }
    if (this.notesLoadPromise) {
      return this.notesLoadPromise;
    }

    this.cancelIdleLoad();
    this.beginLoad();
    this.loadError.set(false);
    this.lastLoadError = null;
    this.notesLoadPromise = this.loadNotesUiInternal().finally(() => {
      this.endLoad();
      this.notesLoadPromise = null;
    });
    return this.notesLoadPromise;
  }

  private loadSidebarUi(): Promise<void> {
    if (this.sidebarModuleRef) {
      return Promise.resolve();
    }
    if (this.sidebarLoadPromise) {
      return this.sidebarLoadPromise;
    }

    this.beginLoad();
    this.loadError.set(false);
    this.lastLoadError = null;
    this.sidebarLoadPromise = this.loadSidebarUiInternal().finally(() => {
      this.endLoad();
      this.sidebarLoadPromise = null;
    });
    return this.sidebarLoadPromise;
  }

  private beginLoad(): void {
    this.pendingLoads += 1;
    this.loading.set(true);
  }

  private endLoad(): void {
    this.pendingLoads = Math.max(0, this.pendingLoads - 1);
    this.loading.set(this.pendingLoads > 0);
  }

  private async loadNotesUiInternal(): Promise<void> {
    try {
      const notes = await import('../shared/overlays/floating-notes.module');
      if (this.destroyRef.destroyed) {
        return;
      }

      this.notesModuleRef = createNgModule(
        notes.FloatingNotesModule,
        this.environmentInjector,
      );

      this.viewContainerRef.createComponent(notes.FloatingNotesWidgetComponent, {
        environmentInjector: this.notesModuleRef.injector,
      });
    } catch (error) {
      this.notesModuleRef?.destroy();
      this.notesModuleRef = null;
      this.lastLoadError = 'notes';
      this.loadError.set(true);
      console.error('Error loading optional floating notes', error);
    }
  }

  private async loadSidebarUiInternal(): Promise<void> {
    try {
      const sidebar = await import('../shared/overlays/collaboration-shell.module');
      if (this.destroyRef.destroyed) {
        return;
      }

      this.sidebarModuleRef = createNgModule(
        sidebar.CollaborationShellModule,
        this.environmentInjector,
      );

      this.sidebarComponentRef = this.viewContainerRef.createComponent(sidebar.SidebarContainerComponent, {
        environmentInjector: this.sidebarModuleRef.injector,
      });
    } catch (error) {
      this.sidebarModuleRef?.destroy();
      this.sidebarModuleRef = null;
      this.lastLoadError = 'sidebar';
      this.loadError.set(true);
      console.error('Error loading optional collaboration sidebars', error);
    }
  }

  private async openQuickAccessFromGesture(): Promise<void> {
    await this.loadSidebarUi();
    this.sidebarComponentRef?.instance.openQuickAccessFromGesture();
  }

  private isMobileTouchGesture(event: PointerEvent): boolean {
    return typeof window !== 'undefined'
      && event.pointerType === 'touch'
      && event.isPrimary
      && window.innerWidth < this.mobileBreakpoint
      && !!window.matchMedia
      && window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  private resetTouchGesture(): void {
    this.touchStartX = null;
    this.touchStartY = null;
    this.swipeGestureActive = false;
  }
}
